import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { TextField } from '@/components/ui/TextField';
import { useFantasySeasons } from '@/features/fantasy/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';

export default function FantasyOnboardingScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const seasonsQuery = useFantasySeasons();
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
  const [teamName, setTeamName] = useState('');

  const seasons = (seasonsQuery.data ?? []).filter(
    (season) => season.competition?.is_fantasy_enabled,
  );
  const canStart = selectedSeasonId !== null && teamName.trim().length >= 3;

  if (!userId) {
    return (
      <Screen>
        <BackHeader title={t('fantasy.onboarding.title')} />
        <EmptyState
          icon="lock-closed-outline"
          title={t('common.signInRequiredTitle')}
          body={t('common.signInRequiredBody')}
          actionLabel={t('auth.signIn')}
          onAction={() => router.push('/auth/sign-in')}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <BackHeader title={t('fantasy.onboarding.title')} />
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 48, gap: 16 }}>
        <AppText variant="caption" className="leading-5">
          {t('fantasy.onboarding.subtitle')}
        </AppText>

        {seasonsQuery.isPending ? (
          <View className="gap-3">
            {[0, 1].map((index) => (
              <Skeleton key={index} className="h-20 w-full" />
            ))}
          </View>
        ) : seasonsQuery.isError ? (
          <ErrorState error={seasonsQuery.error} onRetry={() => seasonsQuery.refetch()} />
        ) : seasons.length === 0 ? (
          <EmptyState
            icon="trophy-outline"
            title={t('fantasy.onboarding.noCompetitionsTitle')}
            body={t('fantasy.onboarding.noCompetitionsBody')}
          />
        ) : (
          <View className="gap-3">
            {seasons.map((season) => {
              const selected = season.id === selectedSeasonId;
              return (
                <Pressable
                  key={season.id}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  onPress={() => setSelectedSeasonId(season.id)}
                >
                  <Card
                    className={`flex-row items-center gap-3 px-4 py-4 ${
                      selected ? 'border-volt bg-volt/10' : ''
                    }`}
                  >
                    <TeamCrest
                      uri={season.competition?.logo_url}
                      name={season.competition?.name ?? season.name}
                      size="lg"
                    />
                    <View className="flex-1">
                      <AppText variant="body" weight="semibold" className="text-ink">
                        {season.competition?.name ?? season.name}
                      </AppText>
                      <AppText variant="micro">{season.name}</AppText>
                    </View>
                    <View
                      className={`h-5 w-5 rounded-full border-2 ${
                        selected ? 'border-volt bg-volt' : 'border-line'
                      }`}
                    />
                  </Card>
                </Pressable>
              );
            })}
          </View>
        )}

        {seasons.length > 0 ? (
          <>
            <TextField
              label={t('fantasy.onboarding.teamNameTitle')}
              placeholder={t('fantasy.onboarding.teamNamePlaceholder')}
              value={teamName}
              onChangeText={setTeamName}
              maxLength={30}
            />
            <Button
              label={t('fantasy.onboarding.start')}
              disabled={!canStart}
              onPress={() => {
                if (selectedSeasonId) {
                  router.push({
                    pathname: '/fantasy/builder',
                    params: { seasonId: selectedSeasonId, teamName: teamName.trim() },
                  });
                }
              }}
            />
          </>
        ) : null}
      </ScrollView>
    </Screen>
  );
}
